(function () {
    if (window.MMProductCardInitialized) {
        return;
    }

    window.MMProductCardInitialized = true;

    /*********************************************************************************************
     * MONEY FORMATTING
     */
    const MONEY_FORMAT =
        (typeof Shopify !== 'undefined' && Shopify.money_format) || '{{amount}}';

    const formatMoney = (amountInCents) => {
        if (
            typeof Shopify !== 'undefined' &&
            typeof Shopify.formatMoney === 'function'
        ) {
            return Shopify.formatMoney(amountInCents, MONEY_FORMAT);
        }

        const locale = document.documentElement
            ? document.documentElement.lang || 'fr-FR'
            : 'fr-FR';
        const currency =
            (typeof Shopify !== 'undefined' &&
                Shopify.currency &&
                Shopify.currency.active) ||
            'EUR';

        try {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
            }).format((amountInCents || 0) / 100).replace(/ /g, '');
        } catch (error) {
            console.warn('[MM][Money] Failed to format money', error);
            return ((amountInCents || 0) / 100).toFixed(2);
        }
    };

    window.mmFormatMoney = formatMoney;

    /*********************************************************************************************
     * UNIFIED VARIANT SELECTOR
     */
    class VariantSelector extends HTMLElement {
        connectedCallback() {
            this._initialized = true;
            this._handleChange = this._handleChange.bind(this);
            this.addEventListener('change', this._handleChange);
            this._applyVariant(false);
        }

        disconnectedCallback() {
            this.removeEventListener('change', this._handleChange);
            this._initialized = false;
        }

        _getVariantData() {
            if (!this._variantData) {
                try {
                    const jsonEl = this.querySelector('script[type="application/json"]');
                    if (!jsonEl) {
                        this._variantData = [];
                    } else {
                        const text = jsonEl.textContent.trim();
                        this._variantData = text ? JSON.parse(text) : [];
                    }
                } catch (error) {
                    console.warn('[MM][Variants] Failed to parse variant data', error);
                    this._variantData = [];
                }
            }
            return this._variantData;
        }

        _getSelectedOptions() {
            const optionGroups = Array.from(
                this.querySelectorAll('[data-variant-option-index]')
            );

            if (optionGroups.length) {
                // Modal mode — groups have explicit data-variant-option-index
                optionGroups.sort((a, b) => {
                    return (parseInt(a.dataset.variantOptionIndex || '0', 10)) -
                           (parseInt(b.dataset.variantOptionIndex || '0', 10));
                });

                return optionGroups
                    .map((group) => {
                        const idx = parseInt(group.dataset.variantOptionIndex || '0', 10) - 1;
                        const selectInput = group.querySelector('select[data-variant-input]');
                        const value = selectInput
                            ? selectInput.value
                            : (group.querySelector('[data-variant-input]:checked') || {}).value || null;
                        return value !== null && value !== '' ? { index: idx, value } : null;
                    })
                    .filter(Boolean);
            }

            // Product page mode — use data-option-position on each input
            const result = [];
            this.querySelectorAll('[data-variant-input]:checked').forEach((input) => {
                const pos = parseInt(input.dataset.optionPosition, 10);
                if (pos && input.value) result.push({ index: pos - 1, value: input.value });
            });
            return result;
        }

        _findVariant(selectedOptions) {
            if (!Array.isArray(selectedOptions) || !selectedOptions.length) return null;

            const variants = this._getVariantData();

            // Exact match: every provided position must match.
            // Quand la sélection ne couvre pas toutes les options du produit (ex: seul le millésime
            // est géré ici, la contenance est gérée par un composant séparé comme mm-product-qtty-selector-v2),
            // plusieurs variants peuvent matcher : on privilégie un variant disponible pour ne pas désactiver
            // le CTA à tort à cause d'un variant en rupture qui matcherait en premier dans l'ordre du produit.
            const matches = (v) =>
                v.options && selectedOptions.every(({ index, value }) => v.options[index] === value);
            const exact = variants.find((v) => matches(v) && v.available) || variants.find(matches);
            if (exact) return exact;

            // Fallback for modal: hidden option may be incompatible with new visible selection.
            // Re-run using only options whose group is visible (no display:none).
            const visibleOptions = selectedOptions.filter(({ index }) => {
                const group = this.querySelector(`[data-variant-option-index="${index + 1}"]`);
                if (!group) return true; // product page — no groups, always "visible"
                return !/display\s*:\s*none/i.test(group.getAttribute('style') || '');
            });

            if (visibleOptions.length && visibleOptions.length < selectedOptions.length) {
                return variants.find(
                    (v) =>
                        v.available &&
                        visibleOptions.every(({ index, value }) => v.options[index] === value)
                ) || null;
            }

            return null;
        }

        _getScope() {
            return (
                this.closest('[data-mm-variants-modal]') ||
                this.closest('form') ||
                null
            );
        }

        _updateContenance(variant) {
            const scope = this._getScope();
            if (!scope) return;

            const jsonEl = scope.querySelector('script[data-variant-contenances]');
            if (!jsonEl) return;

            let contenances = {};
            try { contenances = JSON.parse(jsonEl.textContent.trim()); } catch (_) { return; }

            const contenance = contenances[String(variant.id)] || '';
            const contenanceEl = scope.querySelector('[data-contenance-display]');
            if (!contenanceEl) return;

            if (contenance) {
                contenanceEl.textContent = '(' + contenance + ')';
                contenanceEl.hidden = false;
            } else {
                contenanceEl.hidden = true;
            }
        }

        _applyVariant(dispatchEvent = true) {
            const options = this._getSelectedOptions();
            const variant = this._findVariant(options);

            if (!variant) {
                this._setAvailability(false);
                return;
            }

            this._updateHiddenInput(variant);
            this._updatePrice(variant);
            this._updateModalImage(variant);
            this._updateContenance(variant);
            this._setAvailability(variant.available);
            this._setSoldOut(!variant.available);

            if (dispatchEvent) {
                this.dispatchEvent(
                    new CustomEvent('product:variant-found', {
                        bubbles: true,
                        detail: {
                            data: {
                                variantId: variant.id,
                                available: variant.available,
                            },
                        },
                    })
                );
            }
        }

        _handleChange() {
            this._applyVariant(true);
        }

        _updateHiddenInput(variant) {
            const scope =
                this.closest('[data-mm-variants-modal]') ||
                this.closest('form') ||
                document;

            scope.querySelectorAll('[data-selected-variant-id]').forEach((input) => {
                input.value = String(variant.id);
            });
        }

        _updatePrice(variant) {
            const scope =
                this.closest('[data-mm-variants-modal]') || this.closest('form');

            if (!scope) return;

            scope.querySelectorAll('[data-price]').forEach((priceEl) => {
                priceEl.dataset.price = variant.price;
                priceEl.textContent = formatMoney(variant.price);
            });

            const cp = variant.compare_at_price || 0;
            scope.querySelectorAll('[data-compare-price]').forEach((compareEl) => {
                compareEl.dataset.comparePrice = cp;
                compareEl.textContent = formatMoney(cp);

                const wrapper = compareEl.parentElement;
                if (wrapper && wrapper.classList) {
                    if (cp && cp > variant.price) {
                        wrapper.classList.remove('mm-hidden');
                    } else {
                        wrapper.classList.add('mm-hidden');
                    }
                }
            });
        }

        _updateModalImage(variant) {
            const modal = this.closest('[data-mm-variants-modal]');
            if (!modal) return;

            const img = variant.featured_image;
            if (!img || !img.src) return;

            const picture = modal.querySelector('[data-modal-variant-image]');
            if (!picture) return;

            const imgEl = picture.querySelector('img');
            if (!imgEl) return;

            const base = img.src.split('?')[0];
            imgEl.src = `${base}?width=300`;
            imgEl.srcset = `${base}?width=150 150w, ${base}?width=300 300w, ${base}?width=600 600w`;
            if (img.alt) imgEl.alt = img.alt;
        }

        _setSoldOut(soldOut) {
            const scope = this._getScope();
            if (!scope || scope.querySelector('mm-product-qtty-selector-v2')) return;
            window.MMPurchaseLimits?.setSoldOut(this, soldOut);
        }

        _setAvailability(isAvailable) {
            const scope =
                this.closest('[data-mm-variants-modal]') ||
                this.closest('form') ||
                document;

            scope.querySelectorAll('button[name="add"]').forEach((button) => {
                button.toggleAttribute('disabled', !isAvailable);
            });
        }
    }

    if (!customElements.get('mm-product-variants')) {
        customElements.define('mm-product-variants', VariantSelector);
    }

    if (!customElements.get('variant-selector')) {
        customElements.define('variant-selector', class extends VariantSelector {});
    }

    /*********************************************************************************************
     * GLOBAL QTY CONTROLS
     */
    const getQtyMax = (input) => {
        const inventoryAttr = parseInt(input.dataset.variantInventory, 10);
        if (Number.isFinite(inventoryAttr)) return inventoryAttr;
        const maxAttr = parseInt(input.getAttribute('max'), 10);
        return Number.isFinite(maxAttr) ? maxAttr : 9999;
    };

    const clampQty = (input, value) => {
        const min = parseInt(input.getAttribute('min'), 10) || 1;
        const max = getQtyMax(input);
        const parsed = parseInt(value, 10);
        if (Number.isNaN(parsed)) return min;
        return Math.max(min, Math.min(max, parsed));
    };

    const updatePlusButtonState = (input, qty) => {
        if (!input.id) return;
        const max = getQtyMax(input);
        document
            .querySelectorAll(`[data-qtty-increase="${input.id}"]`)
            .forEach((btn) => {
                btn.toggleAttribute('disabled', qty >= max);
            });
    };

    const applyQtyChange = (input, qty) => {
        input.value = qty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        updatePlusButtonState(input, qty);

        // Update price elements in scope (modal > form > document)
        const scope =
            input.closest('[data-mm-variants-modal]') ||
            input.closest('form') ||
            document;

        scope.querySelectorAll('[data-price]').forEach((priceEl) => {
            const base = parseFloat(priceEl.dataset.price);
            if (!Number.isNaN(base)) {
                priceEl.textContent = formatMoney(base * qty);
            }
        });

        scope.querySelectorAll('[data-compare-price]').forEach((priceEl) => {
            const base = parseFloat(priceEl.dataset.comparePrice);
            if (!Number.isNaN(base)) {
                priceEl.textContent = formatMoney(base * qty);
            }
        });

        // Update display spans
        if (input.id) {
            document
                .querySelectorAll(`[data-qtty-display="${input.id}"]`)
                .forEach((span) => {
                    span.textContent = qty;
                });
        }
    };

    window.mmChangeQtty = (delta, inputId) => {
        if (!inputId) return;
        const input = document.getElementById(inputId);
        if (!input) return;

        const current = parseInt(input.value, 10) || 1;
        const next = clampQty(input, current + parseInt(delta, 10));
        if (next !== current) {
            applyQtyChange(input, next);
        }
    };

    document.addEventListener('click', (event) => {
        const decreaseBtn = event.target.closest('[data-qtty-decrease]');
        if (decreaseBtn) {
            window.mmChangeQtty(-1, decreaseBtn.dataset.qttyDecrease);
            return;
        }

        const increaseBtn = event.target.closest('[data-qtty-increase]');
        if (increaseBtn) {
            window.mmChangeQtty(1, increaseBtn.dataset.qttyIncrease);
        }
    });

    /*********************************************************************************************
     * PURCHASE LIMITS — plafond d'achat tenant compte du panier
     *
     * Le plafond initial (stock ∧ limite d'achat − panier) est rendu par Liquid via
     * 'mm-product-qty-max' : au chargement, AUCUN fetch ici et aucun flash d'UI.
     * Ce module ne recalcule qu'après une mutation panier, depuis `data-qty-base-max`
     * (plafond brut sans le panier) et un seul GET cart.js mutualisé. Vit ici plutôt que
     * dans mm_main_product.js car ce fichier est chargé partout : les modales de variantes
     * des pages collection doivent suivre la même règle que la page produit.
     */
    let cartQtyPromise = null;

    const fetchCartQuantities = () => {
        if (cartQtyPromise) return cartQtyPromise;

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        cartQtyPromise = fetch(`${root}cart.js`)
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((cart) =>
                cart.items.reduce((map, item) => {
                    const id = String(item.variant_id);
                    map[id] = (map[id] || 0) + item.quantity;
                    return map;
                }, {})
            )
            .catch((error) => {
                console.warn('[MM][PurchaseLimit] Failed to fetch cart', error);
                return {};
            })
            .finally(() => {
                cartQtyPromise = null;
            });

        return cartQtyPromise;
    };

    /**
     * Bascule un scope (form produit ou modale) en mode "limite atteinte" : prix, réassurance
     * et CTA d'achat disparaissent au profit du seul bouton explicatif. Une simple classe
     * suffit — le CSS fait le reste — donc l'état survit aux rechargements AJAX partiels et
     * aucun libellé n'est manipulé en JS (pas de risque de double échappement HTML).
     */
    const setLimitReachedState = (scope, limitReached) => {
        const root = scope && scope.closest ? scope.closest('form') || scope : scope;
        if (root && root.classList) {
            root.classList.toggle('mm-limit-reached-scope', limitReached);
        }
    };

    const setSoldOutState = (scope, soldOut) => {
        const root = scope && scope.closest ? scope.closest('form') || scope : scope;
        if (root && root.classList) {
            root.classList.toggle('mm-sold-out-scope', soldOut);
        }
    };

    const refreshPurchaseLimits = async () => {
        const inputs = document.querySelectorAll(
            'input[data-qtty-input][data-qty-base-max]'
        );
        if (!inputs.length) return;

        const cartQuantities = await fetchCartQuantities();

        inputs.forEach((input) => {
            const baseMax = parseInt(input.dataset.qtyBaseMax, 10);
            if (!Number.isFinite(baseMax)) return;

            const cartQty = cartQuantities[String(input.dataset.variantId)] || 0;
            const effectiveMax = Math.max(0, baseMax - cartQty);

            input.setAttribute('max', effectiveMax);
            input.dataset.variantInventory = effectiveMax;

            const currentValue = parseInt(input.value, 10) || 1;
            if (currentValue > effectiveMax) {
                applyQtyChange(input, Math.max(1, effectiveMax));
            }

            // Boutons "+" liés à cet input (page produit + sticky CTA partagent l'id)
            document
                .querySelectorAll(`[data-qtty-increase="${input.id}"]`)
                .forEach((plusBtn) => {
                    plusBtn.toggleAttribute(
                        'disabled',
                        (parseInt(input.value, 10) || 1) >= effectiveMax
                    );
                });

            setLimitReachedState(input, effectiveMax < 1);
        });

        // Après les inputs : le sélecteur V2 raisonne en lots (÷ multiplicateur du pack) et
        // fait donc autorité sur l'état final du scope (ex: 3 unités restantes ≠ 0, mais un
        // pack de 6 reste impossible).
        document
            .querySelectorAll('mm-product-qtty-selector-v2')
            .forEach((el) => el.refreshMaxQty && el.refreshMaxQty(cartQuantities));
    };

    /**
     * Recale le mode "limite atteinte" depuis un document fraîchement rendu par le serveur.
     *
     * Au changement de variante, la section est déjà refetchée pour mettre à jour les blocs
     * [data-product-reload] : ce HTML contient le <form> avec (ou sans) la classe, calculée
     * serveur pour la NOUVELLE variante et le panier réel. On la recopie plutôt que de la
     * réinférer côté client — sinon un passage d'un millésime épuisé à un millésime
     * disponible laisse le CTA "limite atteinte" collé jusqu'au rechargement de page.
     */
    const syncLimitScopeFromDocument = (doc, form) => {
        if (!doc || !form || !form.id) return;

        // Toutes les modales de cartes partagent l'id 'product-modal-form' : si la section
        // refetchée en contient plusieurs, le match est ambigu et on préfère ne rien faire
        // plutôt que de recopier l'état d'un autre produit.
        if (doc.querySelectorAll(`#${CSS.escape(form.id)}`).length !== 1) return;

        const fresh = doc.getElementById(form.id);
        if (!fresh) return;

        form.classList.toggle(
            'mm-limit-reached-scope',
            fresh.classList.contains('mm-limit-reached-scope')
        );
    };

    window.MMPurchaseLimits = {
        refresh: refreshPurchaseLimits,
        fetchCartQuantities,
        setLimitReached: setLimitReachedState,
        setSoldOut: setSoldOutState,
        syncFromDocument: syncLimitScopeFromDocument,
    };

    // Le panier a bougé (ici, dans une modale ou dans le drawer) : le HTML rendu au
    // chargement est périmé. Debounce car une action utilisateur peut émettre plusieurs
    // événements panier à la suite.
    const debouncedRefresh = window.debounce
        ? window.debounce(refreshPurchaseLimits, 300)
        : refreshPurchaseLimits;

    [
        'cart:refreshed',
        'cart:add-to-cart-success',
        'cart:quantity-synced',
        'cart:item-removed',
    ].forEach((eventName) => {
        document.addEventListener(eventName, debouncedRefresh);
    });

    // Changement de variante : le bloc quantité vient d'être re-rendu par le serveur avec le
    // bon plafond, il suffit de le relire. Le sélecteur V2 est exclu — son input n'est pas
    // rechargé (skipIds), c'est le custom element qui pilote son propre scope.
    document.addEventListener('product:product-reload-complete', () => {
        document
            .querySelectorAll(
                'input[data-qtty-input][data-qty-base-max]:not([data-qtty-selector-v2-quantity])'
            )
            .forEach((input) => {
                const max = parseInt(input.getAttribute('max'), 10);
                setLimitReachedState(input, Number.isFinite(max) && max < 1);
            });
    });

    document.addEventListener('change', (event) => {
        const input = event.target.closest('[data-qtty-input]');
        if (!input) return;
        const qty = clampQty(input, input.value);
        if (parseInt(input.value, 10) !== qty) {
            applyQtyChange(input, qty);
        }
    });

    /*********************************************************************************************
     * MODAL LOGIC
     */
    const MODAL_SELECTOR = '[data-mm-variants-modal]';
    const MODAL_CONTENT_SELECTOR = '[data-mm-variants-modal-content]';
    const MODAL_ROOT_ID = 'mm-variants-modals-root';
    let previouslyFocusedElement = null;
    const modalOrigins = new WeakMap();

    const getBody = () => document.body || document.documentElement;

    const hasVisibleModal = () =>
        document.querySelector(`${MODAL_SELECTOR}.is-visible`) !== null;

    const ensureModalRoot = () => {
        const body = getBody();
        if (!body) return null;

        let root = document.getElementById(MODAL_ROOT_ID);
        if (!root) {
            root = document.createElement('div');
            root.id = MODAL_ROOT_ID;
            root.setAttribute('aria-live', 'polite');
            body.appendChild(root);
        }

        return root;
    };

    const rememberModalOrigin = (modal) => {
        if (!modal) return;

        const parent = modal.parentElement;
        if (!parent) return;

        const nextSibling = modal.nextSibling;
        const stored = modalOrigins.get(modal);

        if (stored && stored.parent === parent && stored.nextSibling === nextSibling) {
            return;
        }

        modalOrigins.set(modal, { parent, nextSibling });
    };

    const restoreModalToOrigin = (modal) => {
        const origin = modalOrigins.get(modal);
        if (!origin || !origin.parent) return;

        if (!document.contains(origin.parent)) {
            modalOrigins.delete(modal);
            modal.remove();
            return;
        }

        if (origin.nextSibling && origin.nextSibling.parentElement === origin.parent) {
            origin.parent.insertBefore(modal, origin.nextSibling);
        } else {
            origin.parent.appendChild(modal);
        }
    };

    const moveModalToRoot = (modal) => {
        if (!modal) return;

        rememberModalOrigin(modal);

        const root = ensureModalRoot();
        if (!root || modal.parentElement === root) return;

        root.appendChild(modal);
    };

    const lockScroll = () => {
        getBody().classList.add('mm-variants-modal-open');
    };

    const unlockScroll = () => {
        if (!hasVisibleModal()) {
            getBody().classList.remove('mm-variants-modal-open');
        }
    };

    const closeModal = (modal) => {
        if (!modal) return;

        modal.classList.remove('is-visible');
        modal.setAttribute('aria-hidden', 'true');
        unlockScroll();

        // Attend la fin de la transition CSS avant de cacher l'élément
        const inner = modal.querySelector(MODAL_CONTENT_SELECTOR) || modal;
        inner.addEventListener('transitionend', () => {
            modal.setAttribute('hidden', 'hidden');
            restoreModalToOrigin(modal);
        }, { once: true });

        if (
            previouslyFocusedElement &&
            typeof previouslyFocusedElement.focus === 'function'
        ) {
            previouslyFocusedElement.focus({ preventScroll: true });
            previouslyFocusedElement = null;
        }
    };

    window.showVariantsModal = (productId, purchaseMode = null) => {
        const modal = document.getElementById(`mm-variants-modal-${productId}`);

        if (!modal) return;

        moveModalToRoot(modal);

        previouslyFocusedElement = document.activeElement;

        // Retire hidden d'abord, force un reflow, puis ajoute is-visible
        // pour que la transition CSS puisse jouer depuis l'état initial
        modal.removeAttribute('hidden');
        modal.setAttribute('aria-hidden', 'false');
        modal.offsetHeight; // force reflow — l'élément a maintenant un layout
        modal.classList.add('is-visible');
        lockScroll();

        setTimeout(() => {
            // Re-init RechargesSelector (panels fermés car hidden au chargement)
            modal.querySelectorAll('mm-product-recharges').forEach(el => {
                if (purchaseMode) el.dataset.mode = purchaseMode;
                if (typeof el.initState === 'function') el.initState();
            });

            // Init/refresh Splide pour les sliders de la modal (réassurance, etc.)
            if (typeof Splide !== 'undefined') {
                modal.querySelectorAll('.splide').forEach(el => {
                    if (el._splide) {
                        try { el._splide.refresh(); } catch (e) {}
                    } else {
                        try {
                            const config = el.dataset.splide ? JSON.parse(el.dataset.splide) : {};
                            el._splide = new Splide(el, config).mount();
                        } catch (e) {
                            try { el._splide = new Splide(el).mount(); } catch (e2) {}
                        }
                    }
                });
            }
        }, 50);

        // Fallback : si max-height est encore 0 après que initState + son inner timeout
        // (50ms) ont tourné, on force l'ouverture du panel actif directement
        setTimeout(() => {
            modal.querySelectorAll('mm-product-recharges').forEach(el => {
                const mode = el.dataset.mode || 'one-time';
                const activePanel = el.querySelector(`[data-panel="${mode}"]`);
                if (!activePanel) return;
                const outer = activePanel.querySelector('.mm-recharges-panel-content-outer');
                if (!outer) return;
                const currentH = parseFloat(outer.style.maxHeight) || 0;
                if (currentH === 0) {
                    outer.style.transition = 'none';
                    outer.style.maxHeight = 'none';
                    requestAnimationFrame(() => {
                        const sh = outer.scrollHeight;
                        outer.style.maxHeight = sh > 0 ? sh + 'px' : 'none';
                        requestAnimationFrame(() => { outer.style.transition = ''; });
                    });
                }
            });
        }, 200);

        const content = modal.querySelector(MODAL_CONTENT_SELECTOR);
        if (content) {
            content.focus({ preventScroll: true });
        }

        // Initialize Splide sliders for this modal
        const initializeModalSliders = () => {
            if (typeof window.ProductMediaSliders === 'undefined' ||
                typeof window.ProductMediaSliders.init !== 'function') {
                // ProductMediaSliders not yet loaded, try again after a short delay
                setTimeout(initializeModalSliders, 100);
                return;
            }

            // Find the media container in this modal
            const mediaContainer = modal.querySelector('[data-product-medias]');
            if (!mediaContainer) return;

            // Get the slider instance ID from the data attribute
            const sliderInstanceId = mediaContainer.getAttribute('data-slider-instance');
            if (!sliderInstanceId) return;

            // Wait a bit for DOM to be ready, then initialize
            requestAnimationFrame(() => {
                setTimeout(() => {
                    window.ProductMediaSliders.init(sliderInstanceId, false, modal);
                }, 50);
            });
        };

        // Initialize sliders after modal is shown
        initializeModalSliders();
    };

    window.closeVariantsModal = (productId = null) => {
        let modal = null;

        if (productId) {
            modal = document.getElementById(`mm-variants-modal-${productId}`);
        }

        if (!modal) {
            modal = document.querySelector(`${MODAL_SELECTOR}.is-visible`);
        }

        if (modal) {
            closeModal(modal);
        }
    };

    document.addEventListener('click', (event) => {
        if (
            event.target.hasAttribute('data-mm-variants-modal') &&
            event.target.matches(MODAL_SELECTOR)
        ) {
            closeModal(event.target);
            return;
        }

        const closeTrigger = event.target.closest('[data-close-variants-modal]');
        if (closeTrigger) {
            const modal = closeTrigger.closest(MODAL_SELECTOR);
            closeModal(modal);
            return;
        }

        const modalCta = event.target.closest('.mm-product-cta:not([data-back-in-stock-open])');
        if (modalCta) {
            const modal = modalCta.closest(MODAL_SELECTOR);
            if (modal) {
                closeModal(modal);
            }
        }
    });

    document.addEventListener('keydown', (event) => {
        const modal = document.querySelector(`${MODAL_SELECTOR}.is-visible`);
        if (!modal || document.querySelector('dialog[open]')) return;

        // Fermeture au clavier (Escape)
        if (event.key === 'Escape') {
            event.preventDefault();
            closeModal(modal);
            return;
        }

        // Focus trap dans le modal (Tab / Shift+Tab)
        if (event.key !== 'Tab') return;

        const focusRoot =
            modal.querySelector(MODAL_CONTENT_SELECTOR) || modal;

        if (!focusRoot) return;

        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'textarea:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(',');

        const focusableElements = Array.from(
            focusRoot.querySelectorAll(focusableSelectors)
        ).filter((el) => {
            const isDisabled =
                el.hasAttribute('disabled') ||
                el.getAttribute('aria-disabled') === 'true';
            const style = window.getComputedStyle(el);
            const isHidden =
                style.display === 'none' ||
                style.visibility === 'hidden' ||
                style.opacity === '0';
            return !isDisabled && !isHidden;
        });

        if (!focusableElements.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const currentFocus = document.activeElement;
        const isShiftPressed = event.shiftKey;

        if (!focusRoot.contains(currentFocus)) {
            // Si le focus est sorti du modal, on le ramène au premier élément focusable
            event.preventDefault();
            firstElement.focus({ preventScroll: true });
            return;
        }

        if (!isShiftPressed && currentFocus === lastElement) {
            // Tab sur le dernier élément → retour au premier
            event.preventDefault();
            firstElement.focus({ preventScroll: true });
        } else if (isShiftPressed && currentFocus === firstElement) {
            // Shift+Tab sur le premier élément → retour au dernier
            event.preventDefault();
            lastElement.focus({ preventScroll: true });
        }
    });

    document.addEventListener('shopify:section:unload', () => {
        previouslyFocusedElement = null;
        unlockScroll();
    });
})();
